import { useState, useSyncExternalStore } from 'react';

// A person's round avatar: initials, with their profile photo on top when they have one and the viewer may see
// it (the server answers 404 otherwise, and the initials stay). Photos are cached briefly by the browser; after
// changing your own, `refreshAvatar` makes every place show the new one at once.

const versions = new Map<string, number>();
const listeners = new Set<() => void>();
export function refreshAvatar(userId: string): void {
  versions.set(userId, Date.now());
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function initialsOf(name: string): string {
  // A leading channel marker is not part of the name.
  const parts = name.replace(/^📢\s*/u, '').trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0]!, parts[parts.length - 1]!] : parts;
  return letters.map((p) => Array.from(p)[0]?.toLocaleUpperCase() ?? '').join('') || '?';
}

export function UserAvatar({ userId, name, large = false, className = '' }: { userId: string | null | undefined; name: string; large?: boolean; className?: string }) {
  const version = useSyncExternalStore(subscribe, () => (userId ? (versions.get(userId) ?? 0) : 0));
  const [failed, setFailed] = useState<string | null>(null);
  const src = userId ? `/api/v1/users/${userId}/avatar?size=${large ? 'lg' : 'sm'}${version ? `&v=${version}` : ''}` : null;
  return (
    <span className={`avatar${large ? ' avatar-lg' : ''}${className ? ` ${className}` : ''}`} aria-hidden>
      {initialsOf(name)}
      {src && failed !== src && <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setFailed(src)} />}
    </span>
  );
}
